import { useTranslation } from 'next-i18next';
import { getSupplierUrl, PremiumTiers } from '@bridebook/toolbox';
import gazetteer from '@bridebook/toolbox/src/gazetteer';
import { IUISupplier, SearchBoostValues } from '@bridebook/toolbox/src/types';
import { colors } from '@bridebook/ui';
import { getIsNewSupplier } from '@bridebook/web/app-shared/lib/supplier/utils/get-is-new-supplier';
import { useSearchParams } from '@bridebook/web/lib/search/hooks/query/use-search';
import { getIsCountrySearch } from '@bridebook/web/lib/search/utils/get-is-country-search';
import {
  supplierHasReviews,
  supplierHasSpecialOffer,
  supplierHasVideosUploaded,
  supplierHasWeddingFairOrOpenDay,
} from '@bridebook/web/lib/search/utils/supplier-tiles';
import {
  getSupplierPhotographyStyles,
  getSupplierPhotos,
  getSupplierThumb,
  isTopSpotVenue as getIsTopSpotVenue,
} from '@bridebook/web/lib/supplier/utils';
import { useSupplierBucketSetting } from '@bridebook/web/lib/features/use-supplier-bucket-setting';
interface SearchItemCommonOptions {
  searchLocation: string;
  searchLocationCounty?: string;
}

/**
 * A hook providing all the properties required for search list to work. It expose all the common
 * data used on both - logged in and logged out - lists.
 */

export const useSearchItemCommon = (
  supplier: IUISupplier,
  searchItemCommonOptions: SearchItemCommonOptions,
  supplierUrlParams?: Record<string, string>,
) => {
  const searchParams = useSearchParams();
  const market = supplier.l10n?.country
    ? gazetteer.getMarketByCountry(supplier.l10n?.country)
    : searchParams?.market;
  const { isUnlocked: isFeatureSupplierMarkerUnlocked } = useSupplierBucketSetting(
    'featuredSupplierMarker',
    supplier.type,
    supplier.tier,
  );
  const isCountrySearched = getIsCountrySearch(searchItemCommonOptions.searchLocation);
  const isVenue = supplier.type === 'venue';
  const isMonetizedVenue = market?.flags.monetized && isVenue;
  const { t } = useTranslation('supplierCard');
  const supplierTown = supplier.town;

  const isVenueTier_3 =
    isVenue && (supplier.tier || PremiumTiers.Unverified) >= PremiumTiers.Tier_3;
  const isMarkerVisible = isFeatureSupplierMarkerUnlocked || supplier.supplierBoosted;
  const markerColor = colors.peachRose120;
  const hasSpecialOffer = supplierHasSpecialOffer(supplier) && (supplier?.tier || 0) > 0;
  const hasWeddingFairOrOpenDay =
    supplierHasWeddingFairOrOpenDay(supplier) && (supplier?.tier || 0) > 0;
  const hasVideosUploaded = supplierHasVideosUploaded(supplier, 5);
  const hasVideosInsideNote = supplier.type === 'video' && hasVideosUploaded;

  const hasViewingDates = supplier.hasShowroundViewingDatesAvailable;
  const hasInfoNote = hasViewingDates || supplier.hasQualityPricing;
  const infoNoteText = hasViewingDates
    ? t('infoCard.viewingDatesAvailable')
    : t('infoCard.pricing');
  const isTopSpotVenue = getIsTopSpotVenue(supplier);
  const showTopSpotLayout = isTopSpotVenue && supplier.boost === SearchBoostValues.TopSpot;
  const showCountryTopSpotLayout = supplier.matchedBy?.includes('placeTopSpot') || false;
  const searchBoostForType = supplier.searchBoostForType;
  const isForeign = supplier.isForeign;

  const weddingsHosted = supplier.weddingsHosted || [];
  const shouldDisplayWeddingsHosted = supplier.type !== 'venue' && weddingsHosted.length > 0;

  const hostedWeddingsRange = () => {
    if (supplier.weddingsHosted) {
      if (supplier.weddingsHosted?.length === 1) {
        return `${supplier.weddingsHosted[0]}+`;
      }
      return `${supplier.weddingsHosted[0]}-${supplier.weddingsHosted[1]}`;
    }
  };

  const weddingsHostedLabel = (supplier: IUISupplier) => {
    if (getIsNewSupplier(weddingsHosted)) {
      return t('infoCard.newSupplier');
    }

    switch (supplier.type) {
      case 'photo':
        return t('infoCard.weddingsPhotoShots', {
          weddingsHosted: hostedWeddingsRange(),
        });
      case 'video':
        return t('infoCard.weddingsVideoShots', {
          weddingsHosted: hostedWeddingsRange(),
        });
      case 'florist':
      case 'decoration':
        return t('infoCard.weddingsDecorated', {
          weddingsHosted: hostedWeddingsRange(),
        });
      case 'catering':
        return t('infoCard.weddingsCatered', {
          weddingsHosted: hostedWeddingsRange(),
        });
      case 'music':
        return t('infoCard.weddingsPlayed', {
          weddingsHosted: hostedWeddingsRange(),
        });
      case 'planners':
        return t('infoCard.weddingsPlanned', {
          weddingsHosted: hostedWeddingsRange(),
        });
      /** All the others default to "served" */
      default:
        return t('infoCard.weddingsServed', {
          weddingsHosted: hostedWeddingsRange(),
        });
    }
  };

  return {
    isVenue,
    locationPillValue: supplierTown,
    isTopSpotVenue,

    thumb: getSupplierThumb(supplier),
    photos: getSupplierPhotos(supplier),
    photographyStyles: getSupplierPhotographyStyles(supplier).slice(0, 3),
    pills: [
      { label: infoNoteText, condition: hasInfoNote },

      {
        label: t('infoCard.dealAvailable'),
        condition: hasSpecialOffer || supplier.hasBridebookOffer,
      },
      {
        label: t('infoCard.upcomingEvent'),
        condition: hasWeddingFairOrOpenDay,
      },
      {
        label: weddingsHostedLabel(supplier),
        condition: shouldDisplayWeddingsHosted,
      },
      {
        label: t('infoCard.videosInside', { videosCount: supplier.videosCount }),
        condition: hasVideosInsideNote,
      },
    ],
    showReviews: supplierHasReviews(supplier),
    showFeatureSupplierMarker: isMarkerVisible || supplier.supplierBoosted,
    markerColor,
    isVenueTier_3,
    supplierUrl: getSupplierUrl(supplier, supplierUrlParams),
    t,
    isMonetizedVenue,
    isCountrySearched,
    showTopSpotLayout,
    showCountryTopSpotLayout,
    searchBoostForType,
    isForeign,
  };
};
